// src/components/charting/ToothDiagram.tsx
import React from "react";
import "../../css/ToothDiagram.css";
import largeToothBg from "../../assets/large-tooth-bg.png";
import smallToothBg from "../../assets/small-tooth-bg.png";
import { ToothChartingData } from "../../pages/DentalChartingScreen";

interface ToothDiagramProps {
  toothId: number | null;
  chartingData: ToothChartingData | undefined;
  onSegmentClick: (
    segmentId: number,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  onMissingToothClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const SMALL_TEETH_IDS = new Set([
  53, 52, 51, 61, 62, 63, 83, 82, 81, 71, 72, 73,
]);

// Helper to determine the parameter type for coloring the buttons
const getParameterTypeForSegment = (
  segmentId: number,
): "restoration" | "caries" | "activity" => {
  if ([1, 4, 7, 10, 13].includes(segmentId)) return "restoration";
  if ([2, 5, 8, 11, 14].includes(segmentId)) return "caries";
  return "activity";
};

// Adjusted coordinates for better spacing
const largeToothSegments = [
  { id: 1, top: "18%", left: "38%" },
  { id: 2, top: "18%", left: "50%" },
  { id: 3, top: "18%", left: "62%" },
  { id: 4, top: "38%", left: "82%" },
  { id: 5, top: "50%", left: "82%" },
  { id: 6, top: "62%", left: "82%" },
  { id: 9, top: "82%", left: "62%" },
  { id: 8, top: "82%", left: "50%" },
  { id: 7, top: "82%", left: "38%" },
  { id: 12, top: "62%", left: "18%" },
  { id: 11, top: "50%", left: "18%" },
  { id: 10, top: "38%", left: "18%" },
  { id: 13, top: "44%", left: "41%" },
  { id: 14, top: "44%", left: "50%" },
  { id: 15, top: "44%", left: "59%" },
];
const smallToothSegments = [
  { id: 1, top: "20%", left: "50%" },
  { id: 2, top: "30%", left: "50%" },
  { id: 3, top: "40%", left: "50%" },
  { id: 4, top: "35%", left: "78%" },
  { id: 5, top: "50%", left: "78%" },
  { id: 6, top: "65%", left: "78%" },
  { id: 9, top: "60%", left: "50%" },
  { id: 8, top: "70%", left: "50%" },
  { id: 7, top: "80%", left: "50%" },
  { id: 12, top: "65%", left: "25%" },
  { id: 11, top: "50%", left: "25%" },
  { id: 10, top: "35%", left: "25%" },
];

const ToothDiagram: React.FC<ToothDiagramProps> = ({
  toothId,
  chartingData,
  onSegmentClick,
  onMissingToothClick,
}) => {
  if (!toothId) {
    return (
      <div className="tooth-diagram-container placeholder">
        Select a tooth to view its chart.
      </div>
    );
  }

  const isSmallTooth = SMALL_TEETH_IDS.has(toothId);
  const backgroundImage = isSmallTooth ? smallToothBg : largeToothBg;
  const segments = isSmallTooth ? smallToothSegments : largeToothSegments;

  const isMissing = chartingData?.isMissing;

  return (
    <div className="diagram-wrapper">
      <div
        className="tooth-diagram-container"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <button
          className="tooth-number-button"
          style={
            isSmallTooth
              ? { top: "4%", left: "18%" }
              : { top: "4%", left: "10%" }
          }
          onClick={(e) => onMissingToothClick(e)}
        >
          {chartingData?.missingCode || "P"}
        </button>

        {segments.map((segment) => {
          const segmentData = chartingData?.segments[segment.id];
          const parameterType = getParameterTypeForSegment(segment.id);
          const buttonClass = `segment-button ${parameterType}`;
          // Determine which value to show based on priority
          let displayValue = "";
          if (segmentData?.restoration) displayValue = segmentData.restoration;
          else if (segmentData?.caries) displayValue = segmentData.caries;
          else if (segmentData?.activity) displayValue = segmentData.activity;

          return (
            <button
              key={segment.id}
              className={buttonClass}
              style={{ top: segment.top, left: segment.left }}
              onClick={(e) => onSegmentClick(segment.id, e)}
              disabled={isMissing}
            >
              {displayValue}
            </button>
          );
        })}
      </div>
      <div className="tooth-label">Tooth #: {toothId}</div>
    </div>
  );
};

export default ToothDiagram;
